import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { LayoutDashboard, ListChecks, Users, Smartphone, DollarSign, CheckCircle2 } from 'lucide-react';
export function ManageShowPage() {
  const steps = [{
    title: 'Open the show workspace',
    desc: 'Everything for the gig lives on one record.',
    icon: LayoutDashboard
  }, {
    title: 'Advance the details',
    desc: 'Track times, contacts, guest list, hospitality, and backline.',
    icon: ListChecks
  }, {
    title: 'Keep the team aligned',
    desc: 'Artists, managers, venues, and members see the right information.',
    icon: Users
  }, {
    title: 'Stay mobile on the road',
    desc: 'Use itinerary, docs, and updates from anywhere.',
    icon: Smartphone
  }, {
    title: 'Close the loop financially',
    desc: 'Settlement and internal payouts happen with context intact.',
    icon: DollarSign
  }];
  return <div className="min-h-screen pt-32 pb-24" data-id="element-7258">
      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 mb-20 text-center" data-id="element-7259">
        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-4xl md:text-5xl font-bold text-textMain mb-6 tracking-tight" data-id="element-7260">
          Booking the show is only half the job.
        </motion.h1>
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="text-lg text-textMuted leading-relaxed" data-id="element-7261">
          Booker keeps logistics, contacts, lineup, itinerary, documents, and
          finance tied to the same confirmed show record.
        </motion.p>
      </section>

      {/* Problem Section */}
      <section className="max-w-4xl mx-auto px-6 mb-20" data-id="element-7262">
        <div className="bg-red-500/5 border border-red-500/10 rounded-2xl p-8 md:p-12 text-center" data-id="element-7263">
          <h2 className="text-2xl font-bold text-textMain mb-4" data-id="element-7264">
            After confirmation, details scatter again.
          </h2>
          <p className="text-textMuted leading-relaxed" data-id="element-7265">
            Load-in info, guest lists, parking notes, contracts, deposits, and
            contact details often get fragmented right when execution matters
            most.
          </p>
        </div>
      </section>

      {/* Scenario Story */}
      <section className="max-w-4xl mx-auto px-6 mb-24" data-id="element-7266">
        <div className="bg-surface border border-border rounded-2xl p-8 md:p-12 relative overflow-hidden" data-id="element-7267">
          <div className="absolute top-0 left-0 w-1 h-full bg-primary" data-id="element-7268"></div>
          <div className="prose prose-invert max-w-none" data-id="element-7269">
            <p className="italic text-lg text-textMuted leading-relaxed mb-6" data-id="element-7270">
              Once the date is confirmed, the real work begins: load-in times,
              guest lists, parking, contacts, contracts, deposits, and who still
              needs to know what. Too often, all of that gets scattered again.
            </p>
            <p className="italic text-lg text-textMuted leading-relaxed" data-id="element-7271">
              Booker keeps the show operationally intact from confirmation
              through day-of and final settlement.
            </p>
          </div>
        </div>
      </section>

      {/* Workflow Steps */}
      <section className="max-w-4xl mx-auto px-6 mb-24" data-id="element-7272">
        <div className="space-y-6" data-id="element-7273">
          {steps.map((step, i) => <motion.div key={i} initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="flex items-start gap-6 bg-surface border border-border rounded-2xl p-6" data-id="element-7274">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0" data-id="element-7275">
                <step.icon className="w-6 h-6 text-primary" data-id="element-7276" />
              </div>
              <div data-id="element-7277">
                <h3 className="text-xl font-bold text-textMain mb-2" data-id="element-7278">
                  Step {i + 1} — {step.title}
                </h3>
                <p className="text-textMuted" data-id="element-7279">{step.desc}</p>
              </div>
            </motion.div>)}
        </div>
      </section>

      {/* Outcomes */}
      <section className="max-w-4xl mx-auto px-6 mb-24" data-id="element-7280">
        <div className="bg-surface border border-border rounded-2xl p-8 md:p-12 text-center" data-id="element-7281">
          <h2 className="text-2xl font-bold text-textMain" data-id="element-7282">
            One show record from first offer to final payout.
          </h2>
        </div>
      </section>

      {/* CTA Band */}
      <section className="max-w-4xl mx-auto px-6 text-center" data-id="element-7283">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-7284">
          <Link to="/onboarding" className="px-8 py-3 bg-primary text-background font-bold rounded-lg hover:bg-primaryHover transition-colors" data-id="element-7285">
            Start free
          </Link>
          <Link to="/demo" className="px-8 py-3 bg-surface border border-border text-textMain font-medium rounded-lg hover:bg-surfaceHover transition-colors" data-id="element-7286">
            Book a demo
          </Link>
        </div>
      </section>
    </div>;
}